/**
 * FinanzUstOrganschaftPage — USt-Organschaft im Finanzen-Cockpit
 * (Sektion „USt-Organschaft", Route /finanzen/:firma/ust)
 *
 * Konsolidierte Umsatzsteuer-Auswertung der Organschaft:
 *   - Organträger:     SV-Büro (firma = 'sv_buero_koop')
 *   - Organgesellschaft: LCS GmbH (firma = 'lcs')
 *
 * Diese Ansicht ist ein ENTWURF und nicht zur Abgabe bestimmt. Sie dient als
 * vorläufige Orientierung; die Freigabe erfolgt mit dem Steuerberater (Nils).
 *
 * Eingebettet im Mandanten-Cockpit. Ruhig gehalten (slate/zinc),
 * ein einziger emerald-Akzent. Gleiches Prop-Muster wie FinanzPruefungPage.
 *
 * Prop `firmaKey`:
 *   Wird übergeben, aber die Organschaft fasst SV + LCS immer zusammen
 *   (konsolidierte Sicht). Der Wert wird daher nur informativ genutzt.
 *
 * DATENQUELLE (read-only View, bereits vorhanden — keine Migration):
 *   - v_ust_organschaft_entwurf: monat (date), firma ('sv_buero_koop' | 'lcs'),
 *                                umsatzsteuer (numeric), vorsteuer (numeric),
 *                                zahllast_entwurf (numeric)
 *
 * RLS/Policies bleiben unangetastet (Inhaber-only).
 * Alle Abfragen sind robust gegen fehlende Views/leere Daten (kein Absturz).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Landmark, Loader2, Inbox, Info, AlertTriangle,
} from 'lucide-react';

// ============================================================
// Firmen-Mapping
// ============================================================

export type UstFirmaKey = 'sv' | 'lcs' | 'alle';

const FIRMA_SV = 'sv_buero_koop';
const FIRMA_LCS = 'lcs';

const FIRMA_LABEL: Record<string, string> = {
  sv_buero_koop: 'SV-Büro',
  lcs: 'LCS GmbH',
};

// ============================================================
// Typen
// ============================================================

interface UstRow {
  monat: string | null;
  firma: string | null;
  umsatzsteuer: number | string | null;
  vorsteuer: number | string | null;
  zahllast_entwurf: number | string | null;
}

/** Pro Monat aggregierte Organkreis-Zeile. */
interface MonatsZeile {
  key: string;            // YYYY-MM
  monatDatum: string;     // ISO-Datum des Monatsersten (für Sortierung/Label)
  svUst: number;
  lcsUst: number;
  umsatzsteuerGesamt: number;
  vorsteuerGesamt: number;
  zahllast: number;
}

// ============================================================
// Helpers
// ============================================================

function isViewNotFound(error: unknown): boolean {
  if (!error) return false;
  const e = error as { message?: string; code?: string };
  const msg = (e.message || '') + ' ' + (e.code || '');
  return (
    msg.includes('PGRST205') ||
    msg.includes('schema cache') ||
    msg.includes('relation') ||
    msg.includes('does not exist')
  );
}

function toNum(v: number | string | null | undefined): number {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtEuro(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/** Erzeugt aus einem ISO/Datum-String den Monatsschlüssel YYYY-MM (oder ''). */
function monatsKey(d: string | null): string {
  if (!d || d.length < 7) return '';
  return d.slice(0, 7);
}

function monatLabel(k: string): string {
  if (!k || k.length < 7) return k;
  const m = Number(k.slice(5, 7));
  const y = k.slice(0, 4);
  return m >= 1 && m <= 12 ? `${MONATE[m - 1]} ${y}` : k;
}

/** Schwelle, ab der eine Zahllast dezent als „hoch" markiert wird. */
const HOHE_ZAHLLAST_SCHWELLE = 10000;

// ============================================================
// Daten-Layer
// ============================================================

async function ladeUstOrganschaft(): Promise<UstRow[]> {
  try {
    const { data, error } = await supabase
      .from('v_ust_organschaft_entwurf')
      .select('monat, firma, umsatzsteuer, vorsteuer, zahllast_entwurf')
      .limit(10000);
    if (error) throw error;
    return (data as UstRow[]) || [];
  } catch (err) {
    if (!isViewNotFound(err)) console.error('USt-Organschaft: Laden fehlgeschlagen:', err);
    return [];
  }
}

// ============================================================
// UI-Bausteine
// ============================================================

/** Leerer-Zustand-Block. */
function LeerBlock({ titel, text }: { titel: string; text: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
      <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center mx-auto mb-4">
        <Inbox className="w-6 h-6 text-slate-300" />
      </div>
      <h3 className="text-base font-bold text-slate-900">{titel}</h3>
      <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto leading-relaxed">{text}</p>
    </div>
  );
}

// ============================================================
// Hauptkomponente
// ============================================================

export default function FinanzUstOrganschaftPage({ firmaKey = 'alle' }: { firmaKey?: UstFirmaKey }) {
  // firmaKey wird übergeben (gleiches Prop-Muster), die Organschaft konsolidiert
  // jedoch immer SV + LCS. Eine Referenz vermeidet ungenutzte-Variable-Warnungen.
  void firmaKey;

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<UstRow[]>([]);
  const [jahr, setJahr] = useState<number>(2026);
  const [monatFilter, setMonatFilter] = useState<string>('alle');

  const lade = useCallback(async () => {
    setLoading(true);
    const data = await ladeUstOrganschaft();
    setRows(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    lade();
  }, [lade]);

  // Verfügbare Jahre aus den Daten (absteigend); 2026 immer als Default vorhanden.
  const jahrOptionen = useMemo(() => {
    const set = new Set<number>();
    set.add(2026);
    for (const r of rows) {
      const k = monatsKey(r.monat);
      if (k) set.add(Number(k.slice(0, 4)));
    }
    return Array.from(set).filter((y) => Number.isFinite(y)).sort((a, b) => b - a);
  }, [rows]);

  // Aggregation je Monat (group nach monat), gefiltert auf das gewählte Jahr.
  const monatsZeilen = useMemo<MonatsZeile[]>(() => {
    const map = new Map<string, MonatsZeile>();
    for (const r of rows) {
      const k = monatsKey(r.monat);
      if (!k) continue;
      if (Number(k.slice(0, 4)) !== jahr) continue;

      let z = map.get(k);
      if (!z) {
        z = {
          key: k,
          monatDatum: r.monat || `${k}-01`,
          svUst: 0,
          lcsUst: 0,
          umsatzsteuerGesamt: 0,
          vorsteuerGesamt: 0,
          zahllast: 0,
        };
        map.set(k, z);
      }
      const ust = toNum(r.umsatzsteuer);
      const vst = toNum(r.vorsteuer);
      const zahl = toNum(r.zahllast_entwurf);
      if (r.firma === FIRMA_SV) z.svUst += ust;
      else if (r.firma === FIRMA_LCS) z.lcsUst += ust;
      z.umsatzsteuerGesamt += ust;
      z.vorsteuerGesamt += vst;
      z.zahllast += zahl;
    }
    return Array.from(map.values()).sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  }, [rows, jahr]);

  // Monats-Auswahl: optional auf einen einzelnen Monat einschränken.
  const sichtbareZeilen = useMemo(() => {
    if (monatFilter === 'alle') return monatsZeilen;
    return monatsZeilen.filter((z) => z.key === monatFilter);
  }, [monatsZeilen, monatFilter]);

  // Kumulierte Jahres-Zahllast (über alle Monate des Jahres, unabhängig vom Monatsfilter).
  const jahresZahllast = useMemo(
    () => monatsZeilen.reduce((s, z) => s + z.zahllast, 0),
    [monatsZeilen],
  );

  // Falls der gewählte Monat im neuen Jahr nicht existiert: zurück auf „alle".
  useEffect(() => {
    if (monatFilter !== 'alle' && !monatsZeilen.some((z) => z.key === monatFilter)) {
      setMonatFilter('alle');
    }
  }, [monatsZeilen, monatFilter]);

  return (
    <div className="space-y-5">
      {/* Kopf */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center">
            <Landmark className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">USt-Organschaft</h2>
            <p className="text-xs text-slate-400">
              Konsolidierte Umsatzsteuer — SV-Büro (Organträger) &amp; LCS GmbH (Organgesellschaft)
            </p>
          </div>
        </div>
      </div>

      {/* ENTWURF-Hinweis-Banner (dezent amber) */}
      <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl p-3.5">
        <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-700 leading-relaxed">
          <span className="font-semibold">ENTWURF</span> — vorläufige konsolidierte USt der
          Organschaft SV-Büro + LCS GmbH. Nicht zur Abgabe. Erst mit Steuerberater (Nils) freigeben.
        </div>
      </div>

      {/* Filter: Jahr + optional Monat */}
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={jahr}
          onChange={(e) => { setJahr(Number(e.target.value)); setMonatFilter('alle'); }}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
          aria-label="Jahr"
        >
          {jahrOptionen.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select
          value={monatFilter}
          onChange={(e) => setMonatFilter(e.target.value)}
          disabled={monatsZeilen.length === 0}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400 disabled:opacity-50"
          aria-label="Monat"
        >
          <option value="alle">Alle Monate</option>
          {monatsZeilen.map((z) => (
            <option key={z.key} value={z.key}>{monatLabel(z.key)}</option>
          ))}
        </select>
        {!loading && (
          <span className="text-xs text-slate-400">
            {monatsZeilen.length} Monat(e) im Jahr {jahr}
          </span>
        )}
      </div>

      {/* Inhalt */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : monatsZeilen.length === 0 ? (
        <LeerBlock
          titel="Noch keine konsolidierte USt"
          text="Sobald die Buchungen vollständig sind, erscheint hier die konsolidierte USt der Organschaft (SV-Büro + LCS GmbH)."
        />
      ) : (
        <>
          {/* Zusammenfassungs-Karte: kumulierte Jahres-Zahllast */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Kumulierte Jahres-Zahllast {jahr}
              </div>
              <div
                className={`text-2xl font-bold mt-1 tabular-nums ${
                  jahresZahllast < 0 ? 'text-emerald-700' : 'text-slate-900'
                }`}
              >
                {fmtEuro(jahresZahllast)}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">
                {jahresZahllast < 0
                  ? 'Voraussichtliche Erstattung (Organkreis)'
                  : 'Voraussichtliche Zahllast (Organkreis, Entwurf)'}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Berücksichtigte Firmen
              </div>
              <div className="text-sm font-medium text-slate-700 mt-2 leading-relaxed">
                {FIRMA_LABEL[FIRMA_SV]} (Organträger)<br />
                {FIRMA_LABEL[FIRMA_LCS]} (Organgesellschaft)
              </div>
            </div>
          </div>

          {/* Monats-Tabelle */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">Monat</th>
                    <th className="text-right font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">SV-Büro USt</th>
                    <th className="text-right font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">LCS USt</th>
                    <th className="text-right font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">Umsatzsteuer gesamt</th>
                    <th className="text-right font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">Vorsteuer gesamt</th>
                    <th className="text-right font-semibold text-xs uppercase tracking-wide text-slate-500 px-4 py-2.5 whitespace-nowrap">Zahllast (Organkreis)</th>
                  </tr>
                </thead>
                <tbody>
                  {sichtbareZeilen.map((z) => {
                    const erstattung = z.zahllast < 0;
                    const hoch = z.zahllast >= HOHE_ZAHLLAST_SCHWELLE;
                    const zahllastFarbe = erstattung
                      ? 'text-emerald-700'
                      : hoch
                        ? 'text-rose-700'
                        : 'text-slate-900';
                    return (
                      <tr key={z.key} className="border-t border-slate-50 hover:bg-slate-50/50">
                        <td className="px-4 py-2.5 text-slate-700 font-medium whitespace-nowrap">{monatLabel(z.key)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-600 whitespace-nowrap">{fmtEuro(z.svUst)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-600 whitespace-nowrap">{fmtEuro(z.lcsUst)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{fmtEuro(z.umsatzsteuerGesamt)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-slate-500 whitespace-nowrap">{fmtEuro(z.vorsteuerGesamt)}</td>
                        <td className={`px-4 py-2.5 text-right tabular-nums font-semibold whitespace-nowrap ${zahllastFarbe}`}>
                          <span className="inline-flex items-center justify-end gap-1">
                            {hoch && !erstattung && <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />}
                            {fmtEuro(z.zahllast)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {monatFilter === 'alle' && sichtbareZeilen.length > 1 && (
                  <tfoot>
                    <tr className="border-t border-slate-200 bg-slate-50/60">
                      <td className="px-4 py-2.5 text-slate-700 font-bold whitespace-nowrap">Summe {jahr}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-700 font-semibold whitespace-nowrap">
                        {fmtEuro(sichtbareZeilen.reduce((s, z) => s + z.svUst, 0))}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-700 font-semibold whitespace-nowrap">
                        {fmtEuro(sichtbareZeilen.reduce((s, z) => s + z.lcsUst, 0))}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-700 font-semibold whitespace-nowrap">
                        {fmtEuro(sichtbareZeilen.reduce((s, z) => s + z.umsatzsteuerGesamt, 0))}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-700 font-semibold whitespace-nowrap">
                        {fmtEuro(sichtbareZeilen.reduce((s, z) => s + z.vorsteuerGesamt, 0))}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-900 font-bold whitespace-nowrap">
                        {fmtEuro(sichtbareZeilen.reduce((s, z) => s + z.zahllast, 0))}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Erklärtext zu Innenumsätzen */}
          <div className="flex items-start gap-2 text-xs text-slate-400">
            <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              Innenumsätze SV↔LCS sind als Organschaft nicht umsatzsteuerbar (LCS rechnet SV netto)
              und daher nicht enthalten. Die Zahllast bezieht sich auf den gesamten Organkreis.
            </span>
          </div>
        </>
      )}
    </div>
  );
}
